import type { ChangeEvent } from "react"

import type { RestructuredPermission } from "../../utils/PermissionsHelpers"

/**
 * Generates child keys with prefix for comparison
 */
export const generateChildKeys = (
  childPermissions: Array<{ key: string; obj: RestructuredPermission }>,
  keyPrefix: string,
): string[] => {
  return childPermissions.map(({ key }) => `${keyPrefix}-${key}`)
}

/**
 * Checks if all children are checked
 */
export const areAllChildrenChecked = (childKeys: string[], checkPermissions: string[]): boolean => {
  const checkedChildKeys = childKeys.filter((key) => checkPermissions.includes(key))
  return childKeys.length > 0 && checkedChildKeys.length === childKeys.length
}

/**
 * Checks if some children are checked (but not all)
 */
export const areSomeChildrenChecked = (childKeys: string[], checkPermissions: string[]): boolean => {
  const checkedChildKeys = childKeys.filter((key) => checkPermissions.includes(key))
  return checkedChildKeys.length > 0 && checkedChildKeys.length < childKeys.length
}

/**
 * Handles parent checkbox change logic
 */
export const handleParentCheckboxChange = (
  e: ChangeEvent<HTMLInputElement>,
  childKeys: string[],
  checkPermissions: string[],
  setCheckedPermissions: (permissions: string[]) => void,
  isReadMode: boolean,
  isAdmin: boolean,
): void => {
  if (isReadMode || isAdmin) return

  if (e.target.checked) {
    // Check all children
    const newPermissions = [...new Set([...checkPermissions, ...childKeys])]
    setCheckedPermissions(newPermissions)
  } else {
    // Uncheck all children
    const newPermissions = checkPermissions.filter((perm) => !childKeys.includes(perm))
    setCheckedPermissions(newPermissions)
  }
}

/**
 * Handles child checkbox change logic
 */
export const handleChildCheckboxChange = (
  e: ChangeEvent<HTMLInputElement>,
  prefixedKey: string,
  checkPermissions: string[],
  setCheckedPermissions: (permissions: string[]) => void,
): void => {
  if (e.target?.checked === true) {
    setCheckedPermissions([...checkPermissions, prefixedKey])
  } else {
    setCheckedPermissions(checkPermissions.filter((perm) => perm !== prefixedKey))
  }
}

/**
 * Builds child permissions array from parent object
 */
export const buildChildPermissions = (
  parentObj: RestructuredPermission,
): Array<{ key: string; obj: RestructuredPermission }> => {
  const childPermissions: Array<{ key: string; obj: RestructuredPermission }> = []

  if (parentObj.children) {
    Object.entries(parentObj.children).forEach(([childKey, childObj]) => {
      childPermissions.push({ key: childKey, obj: childObj })
    })
  }

  return childPermissions
}
